import { fetchWithRetry as fetch, responseError } from './apiError';
import { eventService } from './eventService';
import { tokenService } from './tokenService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const editorPrepareRequests = new Map<string, Promise<DesignEditorPrepareResponse>>();
const editorPrepareCache = new Map<string, DesignEditorPrepareResponse>();
const editorLatestVersions = new Map<string, number>();

function editorPrepareKey(projectId: string, pageId: string, pageVersion: number): string {
  return `${projectId}:${pageId}:${pageVersion}`;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

export function invalidateEditorPrepareCache(
  projectId: string,
  pageId: string,
  currentVersion: number,
): void {
  const prefix = `${projectId}:${pageId}:`;

  editorLatestVersions.set(prefix, Math.max(editorLatestVersions.get(prefix) || 0, currentVersion));

  Array.from(editorPrepareCache.keys()).forEach((key) => {
    const cachedVersion = Number(key.slice(prefix.length));

    if (key.startsWith(prefix) && cachedVersion < currentVersion) editorPrepareCache.delete(key);
  });
}

// eslint-disable-next-line max-statements
async function requestEditorPrepare(
  projectId: string,
  pageId: string,
): Promise<DesignEditorPrepareResponse> {
  const token = tokenService.getAccessToken();

  for (;;) {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/editor/prepare`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      },
    );

    if (response.status === 202) {
      const retryAfter = Number(response.headers.get('Retry-After') || '2');

      await wait(Math.max(250, retryAfter * 1000));
      continue;
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const detail = body.detail?.message || body.detail || 'Failed to prepare the visual editor';
      const error = new Error(detail) as Error & { status?: number; agentRequired?: boolean };

      error.status = response.status;
      error.agentRequired = Boolean(body.detail?.agent_required);
      throw error;
    }

    return response.json();
  }
}

// eslint-disable-next-line max-statements
export function prepareEditorSingleFlight(
  projectId: string,
  pageId: string,
  pageVersion: number,
): Promise<DesignEditorPrepareResponse> {
  const key = editorPrepareKey(projectId, pageId, pageVersion);
  const cached = editorPrepareCache.get(key);

  if (cached) return Promise.resolve(cached);
  const active = editorPrepareRequests.get(key);

  if (active) return active;
  const request = requestEditorPrepare(projectId, pageId)
    .then((result) => {
      invalidateEditorPrepareCache(projectId, pageId, result.version);
      const latestVersion = editorLatestVersions.get(`${projectId}:${pageId}:`) || result.version;

      if (result.version >= latestVersion) {
        editorPrepareCache.set(editorPrepareKey(projectId, pageId, result.version), result);
      }

      return result;
    })
    .finally(() => editorPrepareRequests.delete(key));

  editorPrepareRequests.set(key, request);

  return request;
}

export interface DesignPage {
  id: string;
  page_name: string;
  jsx_content: string;
  compiled_html?: string;
  has_compiled_html?: boolean;
  has_jsx_content?: boolean;
  /** Server-side verdict on compiled_html; false means render the JSX fallback instead. */
  preview_ready?: boolean;
  version: number;
  updated_at: string | null;
  preview_revision?: string | null;
  /** Client-only revision of the loaded JSX/HTML, which may trail SSE metadata. */
  loaded_content_revision?: string;
  generation_failed?: boolean;
  generation_in_progress?: boolean;
  sections_done?: number;
  sections_total?: number;
  generation_phase?: string;
  /** Stable across preview updates and final-save version changes; new on regeneration. */
  generation_run_id?: string | null;
  /** Current stage or recovery explanation supplied by the generation worker. */
  generation_message?: string | null;
  screenshot_url?: string;
  screenshot_thumb_url?: string;
  responsive_screenshots?: Record<string, { full: string; thumb: string }>;
  /** The page's planned background colour (design token `bg`), or '' when the plan names none. */
  preview_background?: string;
  credit_blocked?: boolean;
  /** Credits still needed to finish this page — present only while credit-blocked. */
  credit_required?: number;
  missing_sections_json?: string[] | null;
  section_plan_json?: unknown;
  section_sources_json?: unknown;
  planned?: boolean;
  editor_ready?: boolean;
  editor_manifest_version?: number | null;
}

export type DesignEditorOperation = {
  kind: 'set_style' | 'remove_style' | 'set_text';
  property?: string;
  value?: string | number | null;
};

export interface DesignEditorManifestNode {
  node_id: string;
  native_tag: string;
  component_name?: string | null;
  source_path: string;
  section_key?: string | null;
  ast_route: string;
  source_hash: string;
  ancestor_chain: string[];
  dom_routes?: number[][];
  rendered_instances?: {
    instance_index: number;
    rendered_text: string;
    computed_styles: Record<string, string>;
    dom_route: number[];
  }[];
  supported_capabilities: {
    set_style: boolean;
    remove_style: boolean;
    set_text: boolean;
  };
  rendered_text?: string | null;
}

export interface DesignEditorPrepareResponse {
  page_id: string;
  page_name: string;
  version: number;
  manifest: { version: number; page_id: string; page_version: number; nodes: DesignEditorManifestNode[] };
  manifest_version: number;
  protocol_version: number;
  frame_nonce: string;
  compiled_html: string;
  agent_credit_estimate?: number;
  agent_credit_breakdown?: { chat: number; component_revision: number };
  changed_properties?: string[];
  affected_pages?: { id: string; page_name: string; version: number }[];
  change_summary?: string;
  repository_sync_status?: 'synced' | 'pending';
  repository_sync_message?: string;
  preview_refresh_status?: 'completed' | 'queued';
  video_refresh_status?: 'queued_if_landing_page';
  preview_compiled_html?: string;
}

interface DesignEditorSaveJob {
  job_id: string;
  status: 'queued' | 'processing' | 'persisted' | 'completed' | 'failed' | 'cancelled';
  progress_message?: string | null;
  result?: (Partial<DesignEditorPrepareResponse> & {
    agent_required?: boolean;
    status_code?: number;
  }) | null;
  error?: string | null;
  poll_after_ms?: number;
}

type EditorSaveProgress = (message: string) => void;

function editorRequestId(): string {
  return globalThis.crypto?.randomUUID?.()
    || `editor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function parseEditorError(
  response: Awaited<ReturnType<typeof fetch>>,
  fallback: string,
): Promise<Error> {
  const body = await response.json().catch(() => ({})) as {
    detail?: string | { message?: string; agent_required?: boolean };
  };
  const structured = typeof body.detail === 'object' ? body.detail : null;
  const detail = typeof body.detail === 'string' ? body.detail : (structured?.message || fallback);
  const error = new Error(detail) as Error & { status?: number; agentRequired?: boolean };

  error.status = response.status;
  error.agentRequired = Boolean(structured?.agent_required);

  return error;
}

// eslint-disable-next-line max-statements
async function waitForEditorSaveJob(
  projectId: string,
  pageId: string,
  accepted: DesignEditorSaveJob,
  onProgress?: EditorSaveProgress,
  signal?: globalThis.AbortSignal,
): Promise<DesignEditorPrepareResponse> {
  const token = tokenService.getAccessToken();
  let job = accepted;

  for (;;) {
    if (signal?.aborted) throw new globalThis.DOMException('Design save cancelled', 'AbortError');
    onProgress?.(job.progress_message || 'Waiting for an editor worker…');
    if (job.status === 'cancelled') {
      throw new globalThis.DOMException('Design save cancelled', 'AbortError');
    }
    if (job.status === 'failed') {
      const error = Object.assign(new Error(job.error || 'Failed to save visual edits'), {
        status: job.result?.status_code,
        agentRequired: Boolean(job.result?.agent_required),
        saveCommitted: Boolean(job.result?.version),
      });

      throw error;
    }
    if (job.status === 'completed') {
      const version = Number(job.result?.version || 0);

      if (!version) throw new Error('The editor worker returned an incomplete save result');
      invalidateEditorPrepareCache(projectId, pageId, version);
      const prepared = await prepareEditorSingleFlight(projectId, pageId, version);

      return { ...prepared, ...(job.result || {}) };
    }

    await wait(Math.max(250, Number(job.poll_after_ms || 750)));
    if (signal?.aborted) throw new globalThis.DOMException('Design save cancelled', 'AbortError');
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/editor/jobs/${job.job_id}`,
      {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal,
      },
    );

    if (!response.ok) throw await parseEditorError(response, 'Failed to read editor save progress');
    job = await response.json();
  }
}

/**
 * Live credit picture for a project's credit-blocked design pages. Recomputed
 * by the backend on every fetch/broadcast, so a top-up shrinks the shortfall
 * without a page reload. Absent when nothing is credit-blocked.
 */
export interface DesignCreditStatus {
  blocked_pages: string[];
  available_credits: number;
  required_credits: number;
  shortfall_credits: number;
  per_section_cost: number;
  /** The cheapest blocked page: a resumed run designs as many pages as the balance allows, cheapest first. */
  next_step_credits?: number;
  next_step_shortfall_credits?: number;
}

/** Server-computed plan coverage: planned pages split by whether a design exists. */
export interface DesignPageInventory {
  planned: string[];
  ready: string[];
  missing: string[];
  missing_count: number;
  credit_blocked: string[];
  generating: boolean;
  /** "Generate All Pages (N missing)", built by the server's one label function. */
  generate_label: string;
}

export interface DesignPagesResponse {
  project_id: string;
  design_pages: DesignPage[];
  credit_status?: DesignCreditStatus | null;
  page_inventory?: DesignPageInventory | null;
  /** The first page's live URL once it is published (slot for the live-link work); absent until then. */
  live_url?: string | null;
}

export interface DeletedPageSnapshot {
  page_name: string;
  jsx_content: string;
  compiled_html: string;
  version: number;
  auto_generated: boolean;
  screenshot_url?: string | null;
  screenshot_thumb_url?: string | null;
  section_plan_json?: unknown;
  section_sources_json?: unknown;
  section_hashes_json?: unknown;
  single_page?: boolean;
}

/** Opaque task row captured by batch-delete; the backend serializes Task
 *  columns as-is and the restore endpoint re-hydrates them. The frontend
 *  carries this through the redo stack without inspecting the contents. */
export type DeletedTaskSnapshot = Record<string, unknown>;

export interface VersionEntry {
  version: number;
  created_at: string | null;
  is_current: boolean;
}

// eslint-disable-next-line max-statements
async function fetchDesignPages(
  projectId: string, light = false, includePlanned = false,
): Promise<DesignPagesResponse> {
  const token = tokenService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers.Authorization = `Bearer ${token}`;

  const params = new URLSearchParams();

  if (light) params.set('light', 'true');
  if (includePlanned) params.set('include_planned', 'true');
  const qs = params.toString();
  const url = `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers });

  if (!response.ok) throw await responseError(response);

  return response.json();
}

async function fetchDesignPage(
  projectId: string,
  pageId: string,
): Promise<DesignPage> {
  const token = tokenService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}`;
  const response = await fetch(url, { headers });

  if (!response.ok) throw await responseError(response);

  return response.json();
}

function subscribeToDesignPages(
  projectId: string,
  onUpdate: (pages: DesignPage[]) => void,
): () => void {
  eventService.connect(projectId);

  return eventService.subscribe('design_pages', (data: unknown) => {
    const payload = data as { type: string; design_pages: DesignPage[] };

    if (payload && payload.type === 'design_pages') {
      payload.design_pages.forEach((page) => invalidateEditorPrepareCache(projectId, page.id, page.version));
      onUpdate(payload.design_pages);
    }
  });
}

export const designService = {
  getDesignPages: fetchDesignPages,
  getDesignPage: fetchDesignPage,
  prepareEditor: prepareEditorSingleFlight,
  // eslint-disable-next-line max-statements
  patchEditorElement: async (
    projectId: string,
    pageId: string,
    nodeId: string,
    baseVersion: number,
    operations: DesignEditorOperation[],
    onProgress?: EditorSaveProgress,
    signal?: globalThis.AbortSignal,
    onJobAccepted?: (jobId: string) => void,
  ): Promise<DesignEditorPrepareResponse> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/editor/elements/${nodeId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Idempotency-Key': editorRequestId(),
        },
        body: JSON.stringify({ base_version: baseVersion, operations }),
        signal,
      },
    );

    if (!response.ok) throw await parseEditorError(response, 'Failed to save visual edits');
    if (response.status === 202) {
      const accepted: DesignEditorSaveJob = await response.json();

      onJobAccepted?.(accepted.job_id);

      return waitForEditorSaveJob(projectId, pageId, accepted, onProgress, signal);
    }
    const result: DesignEditorPrepareResponse = await response.json(); // Backward-compatible rollout.

    invalidateEditorPrepareCache(projectId, pageId, result.version);
    editorPrepareCache.set(editorPrepareKey(projectId, pageId, result.version), result);

    return result;
  },
  cancelEditorSave: async (projectId: string, pageId: string, jobId: string): Promise<boolean> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/editor/jobs/${jobId}/cancel`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      },
    );

    if (response.status === 409) return false;
    if (!response.ok) {
      throw await parseEditorError(response, 'Failed to stop the design save');
    }

    return true;
  },
  subscribeToUpdates: subscribeToDesignPages,
  deleteDesignPage: async (projectId: string, pageId: string): Promise<void> => {
    const token = tokenService.getAccessToken();

    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) throw await responseError(response);
  },
  batchDeleteDesignPages: async (
    projectId: string, pageIds: string[],
  ): Promise<{
    deleted_pages: number;
    deleted_logs: number;
    deleted_chat: number;
    deleted_tasks: number;
    page_names: string[];
    snapshot: DeletedPageSnapshot[];
    tasks_snapshot: DeletedTaskSnapshot[];
  }> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/batch-delete`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_ids: pageIds }),
      },
    );

    if (!response.ok) throw await responseError(response);

    return response.json();
  },
  restoreDesignPages: async (
    projectId: string, pages: DeletedPageSnapshot[], tasks?: DeletedTaskSnapshot[],
  ): Promise<{ restored: { id: string; page_name: string }[]; skipped: string[]; restored_tasks: number }> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/restore`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages, tasks: tasks ?? [] }),
      },
    );

    if (!response.ok) throw await responseError(response);

    return response.json();
  },
  getPageVersions: async (projectId: string, pageId: string): Promise<VersionEntry[]> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/versions`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (!response.ok) throw await responseError(response);

    const data = await response.json();

    return data.versions;
  },
  restorePageVersion: async (projectId: string, pageId: string, version: number): Promise<void> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/versions/${version}/restore`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      },
    );

    if (!response.ok) throw await responseError(response);
  },
  getPageVersionContent: async (
    projectId: string, pageId: string, version: number,
  ): Promise<{ version: number; jsx_content: string; compiled_html: string; is_current: boolean }> => {
    const token = tokenService.getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/v1/projects/${projectId}/design-pages/${pageId}/versions/${version}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (!response.ok) throw await responseError(response);

    return response.json();
  },
};
